import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DisclosureSection, EmptyState } from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import type { PortalBootstrapData } from '../types/window';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';
import { NOTIFICATION_EVENT_LABELS } from './notificationPreferenceLabels';
import { formatNotificationInboxTitle } from './notificationInboxRowTitle';
import {
  countUnreadNotifications,
  partitionNotificationsByReadState,
} from './notificationInboxFilters';
import type { NotificationInboxItem, NotificationInboxListPayload } from './notificationInboxTypes';
import letterClosedIcon from '../../../../../media/icons/letter-closed.svg';
import letterOpenedIcon from '../../../../../media/icons/letter-opened.svg';

type NotificationInboxTabProps = {
  bootstrap: PortalBootstrapData;
  onSessionExpired: () => void;
  onUnreadCountChange?: (count: number) => void;
};

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const renderSectionLabel = (label: string, count: number): string =>
  count > 0 ? `${label} (${count})` : label;

const formatOccurredAt = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const NotificationInboxTab = ({
  bootstrap,
  onSessionExpired,
  onUnreadCountChange,
}: NotificationInboxTabProps) => {
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState('');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [items, setItems] = useState<NotificationInboxItem[]>([]);
  const [unreadExpanded, setUnreadExpanded] = useState(true);
  const [readExpanded, setReadExpanded] = useState(false);
  const [markingId, setMarkingId] = useState<number | null>(null);

  const eventLabels = useMemo(() => NOTIFICATION_EVENT_LABELS(), []);
  const baseUrl = bootstrap.restUrls.accountingHub;

  const { unread: unreadItems, read: readItems } = useMemo(
    () => partitionNotificationsByReadState(items),
    [items],
  );

  const syncUnreadCount = useCallback(
    (nextItems: NotificationInboxItem[]) => {
      onUnreadCountChange?.(countUnreadNotifications(nextItems));
    },
    [onUnreadCountChange],
  );

  const loadNotifications = useCallback(async () => {
    if (!hasLoadedOnceRef.current) {
      setLoadState('loading');
      setLoadError('');
    }

    try {
      const params = new URLSearchParams({
        page: '1',
        per_page: '50',
        status: 'all',
      });
      const response = await fetchWithNonce(`${baseUrl}notifications?${params.toString()}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'X-WP-Nonce': bootstrap.nonce },
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (!response.ok) {
        setLoadState('error');
        setLoadError(await parseApiError(response));
        return;
      }

      const json = (await response.json()) as { data?: NotificationInboxListPayload };
      const notifications = json.data?.notifications ?? [];
      setItems(notifications);
      syncUnreadCount(notifications);
      hasLoadedOnceRef.current = true;
      setHasLoadedOnce(true);
      setLoadState('ready');
    } catch {
      setLoadState('error');
      setLoadError(__('Impossible de charger les notifications.'));
    }
  }, [baseUrl, bootstrap.nonce, onSessionExpired, syncUnreadCount]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  const updateReadState = (
    recipientId: number,
    readAt: string | null,
  ) => {
    setItems((current) => {
      const next = current.map((item) =>
        item.recipient_id === recipientId ? { ...item, read_at: readAt } : item,
      );
      syncUnreadCount(next);
      return next;
    });
  };

  const markRead = async (recipientId: number) => {
    setMarkingId(recipientId);
    try {
      const response = await fetchWithNonce(`${baseUrl}notifications/${recipientId}/read`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-WP-Nonce': bootstrap.nonce },
      });
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      if (!response.ok) {
        return;
      }
      updateReadState(recipientId, new Date().toISOString());
    } finally {
      setMarkingId(null);
    }
  };

  const markUnread = async (recipientId: number) => {
    setMarkingId(recipientId);
    try {
      const response = await fetchWithNonce(`${baseUrl}notifications/${recipientId}/unread`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-WP-Nonce': bootstrap.nonce },
      });
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      if (!response.ok) {
        return;
      }
      updateReadState(recipientId, null);
    } finally {
      setMarkingId(null);
    }
  };

  const handleLetterIconClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: NotificationInboxItem,
  ) => {
    event.stopPropagation();
    if (markingId === item.recipient_id) {
      return;
    }
    if (item.read_at === null) {
      void markRead(item.recipient_id);
      return;
    }
    void markUnread(item.recipient_id);
  };

  const handleNotificationNavigate = (item: NotificationInboxItem) => {
    const url = item.action?.url?.trim() ?? '';
    if (url === '' || item.action?.authorized !== true) {
      return;
    }

    if (item.read_at === null) {
      void markRead(item.recipient_id);
    }

    window.location.assign(url);
  };

  const renderNotificationRows = (sectionItems: NotificationInboxItem[]) => (
    <ul className="notifications-inbox__list">
      {sectionItems.map((item) => {
        const fallbackLabel = eventLabels[item.event_code]?.label ?? item.event_code;
        const missionContextLabel = item.display?.mission_context_label?.trim() ?? '';
        const title = formatNotificationInboxTitle(
          item.event_code,
          missionContextLabel,
          fallbackLabel,
        );
        const isUnread = item.read_at === null;
        const canNavigate = item.action?.authorized === true && (item.action?.url?.trim() ?? '') !== '';
        return (
          <li key={item.recipient_id}>
            <div
              role={canNavigate ? 'button' : undefined}
              tabIndex={canNavigate ? 0 : undefined}
              className={[
                'notifications-inbox__item',
                isUnread ? 'notifications-inbox__item--unread' : '',
                canNavigate ? 'notifications-inbox__item--actionable' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={canNavigate ? () => handleNotificationNavigate(item) : undefined}
              onKeyDown={
                canNavigate
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleNotificationNavigate(item);
                      }
                    }
                  : undefined
              }
            >
              <button
                type="button"
                className="notifications-inbox__item-icon-button"
                disabled={markingId === item.recipient_id}
                onClick={(event) => handleLetterIconClick(event, item)}
                aria-label={
                  isUnread ? __('Marquer comme lu') : __('Marquer comme non lu')
                }
              >
                <img
                  src={isUnread ? letterClosedIcon : letterOpenedIcon}
                  alt=""
                  width="16"
                  height="16"
                />
              </button>
              {isUnread ? (
                <span className="notifications-inbox__item-unread-dot" aria-hidden />
              ) : null}
              <div className="notifications-inbox__item-body">
                <span className="notifications-inbox__item-title">{title}</span>
              </div>
              <span className="notifications-inbox__item-meta">{formatOccurredAt(item.occurred_at)}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );

  if (!hasLoadedOnce && (loadState === 'loading' || loadState === 'idle')) {
    return (
      <div className="container-style notifications-inbox">
        <p>{__('Chargement…')}</p>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="container-style notifications-inbox">
        <p className="accounting-hub-notice accounting-hub-notice-error">{loadError}</p>
        <button type="button" className="btn-secondary" onClick={() => void loadNotifications()}>
          {__('Réessayer')}
        </button>
      </div>
    );
  }

  return (
    <div className="container-style notifications-inbox">
      {items.length === 0 ? (
        <EmptyState
          title={__('Aucune notification')}
          description={__('Les notifications liées à votre travail apparaîtront ici.')}
        />
      ) : (
        <ul className="tpct-disclosure-list">
          <li className="tpct-disclosure-list__item">
            <DisclosureSection
              label={renderSectionLabel(__('Non lu'), unreadItems.length)}
              labelClassName="h4"
              isExpanded={unreadExpanded}
              onToggle={() => setUnreadExpanded((current) => !current)}
            >
              {unreadItems.length === 0 ? (
                <p className="notifications-inbox__section-empty">
                  {__('Vous n’avez pas de notification non lue.')}
                </p>
              ) : (
                renderNotificationRows(unreadItems)
              )}
            </DisclosureSection>
          </li>
          <li className="tpct-disclosure-list__item">
            <DisclosureSection
              label={renderSectionLabel(__('Lu'), readItems.length)}
              labelClassName="h4"
              isExpanded={readExpanded}
              onToggle={() => setReadExpanded((current) => !current)}
            >
              {readItems.length === 0 ? (
                <p className="notifications-inbox__section-empty">
                  {__('Vous n’avez pas de notification lue.')}
                </p>
              ) : (
                renderNotificationRows(readItems)
              )}
            </DisclosureSection>
          </li>
        </ul>
      )}
    </div>
  );
};

export default NotificationInboxTab;
